import { useEffect, useLayoutEffect, useState } from "react";

const LINE_TOP_TOLERANCE = 4;

const getValidClientRects = element => {
	if (!element) {
		return [];
	}

	return [...element.getClientRects()].filter(rect => rect.width > 0 && rect.height > 0);
};

const clusterLineTops = rects => {
	const lineTops = [];

	rects.forEach(rect => {
		const hasMatchingLine = lineTops.some(
			lineTop => Math.abs(lineTop - rect.top) <= LINE_TOP_TOLERANCE
		);

		if (!hasMatchingLine) {
			lineTops.push(rect.top);
		}
	});

	return lineTops;
};

const getLineHeight = element => {
	const styles = window.getComputedStyle(element);
	let lineHeight = parseFloat(styles.lineHeight);

	if (Number.isNaN(lineHeight)) {
		lineHeight = parseFloat(styles.fontSize) * 1.2;
	}

	return lineHeight;
};

/**
 * Returns the number of rendered lines for an element, based on `getClientRects()`.
 * Works reliably for inline text (unlike height / line-height on `display: inline`).
 *
 * @param {HTMLElement | null} element - Target element to measure
 * @returns {number} Line count (minimum 1)
 */
export const getElementLineCount = element => {
	const lineRects = getValidClientRects(element);

	if (lineRects.length === 0) {
		return 1;
	}

	return Math.max(1, clusterLineTops(lineRects).length);
};

/**
 * Returns the `top` position of the last rendered line for an element.
 *
 * @param {HTMLElement | null} element - Target element to measure
 * @returns {number}
 */
export const getElementLastLineTop = element => {
	const lineRects = getValidClientRects(element);

	if (lineRects.length === 0) {
		return 0;
	}

	return Math.max(...lineRects.map(rect => rect.top));
};

/**
 * Returns `true` when text spans a single line and the suffix/icon sits on the next line.
 *
 * @param {HTMLElement | null} textElement - Text-only wrapper
 * @param {HTMLElement | null} suffixElement - Icon/action wrapper placed after the text
 * @returns {boolean}
 */
export const isTextOneLineWithSuffixOnNextLine = (textElement, suffixElement) => {
	if (!textElement || !suffixElement) {
		return false;
	}

	const lineHeight = getLineHeight(textElement);

	if (textElement.offsetHeight > lineHeight * 1.5) {
		return false;
	}

	const textBottom = textElement.offsetTop + textElement.offsetHeight;
	const suffixTop = suffixElement.offsetTop;

	return suffixTop >= textBottom - LINE_TOP_TOLERANCE;
};

/**
 * Whether to apply left margin on the suffix/icon.
 * Margin is skipped only when text is one line and the icon wraps alone to line 2.
 * All other layouts (text multiline, icon on same line as text, etc.) get the margin.
 *
 * @param {HTMLElement | null} textElement - Text-only wrapper
 * @param {HTMLElement | null} suffixElement - Icon/action wrapper
 * @returns {boolean}
 */
export const shouldAddSuffixMarginLeft = (textElement, suffixElement) => {
	if (!suffixElement) {
		return false;
	}

	return !isTextOneLineWithSuffixOnNextLine(textElement, suffixElement);
};

const scheduleMeasure = callback => {
	requestAnimationFrame(() => {
		requestAnimationFrame(callback);
	});
};

/**
 * Tracks whether a title suffix (e.g. info icon) should receive `margin-left`.
 *
 * Usage:
 * ```jsx
 * const textRef = useRef(null);
 * const suffixRef = useRef(null);
 * const shouldAddMargin = useSuffixMarginLeft(textRef, suffixRef);
 *
 * return (
 *   <h3 className={shouldAddMargin ? "title--icon-margin" : undefined}>
 *     <span ref={textRef}>{text}</span>
 *     <span ref={suffixRef}>{suffix}</span>
 *   </h3>
 * );
 * ```
 *
 * - Attach `textRef` to the text-only wrapper (not the icon).
 * - Attach `suffixRef` to a wrapper around the icon/action sibling.
 * - Margin is omitted only when text is 1 line and the icon wraps to line 2 alone.
 * - Re-measures on resize, parent resize, font load, and after layout (double rAF).
 *
 * @param {React.RefObject<HTMLElement>} textRef - Ref on the text element
 * @param {React.RefObject<HTMLElement>} suffixRef - Ref on the suffix/icon wrapper
 * @returns {boolean} `true` when `margin-left` should be applied to the suffix
 */
const useSuffixMarginLeft = (textRef, suffixRef) => {
	const [shouldAddMargin, setShouldAddMargin] = useState(false);

	const measure = () => {
		const textElement = textRef.current;
		const suffixElement = suffixRef?.current;

		if (!textElement) {
			return;
		}

		setShouldAddMargin(shouldAddSuffixMarginLeft(textElement, suffixElement));
	};

	useLayoutEffect(() => {
		const textElement = textRef.current;

		if (!textElement) {
			return undefined;
		}

		measure();
		scheduleMeasure(measure);

		const resizeObserver = new ResizeObserver(measure);
		resizeObserver.observe(textElement);

		const suffixElement = suffixRef?.current;

		if (suffixElement) {
			resizeObserver.observe(suffixElement);
		}

		if (textElement.parentElement) {
			resizeObserver.observe(textElement.parentElement);
		}

		window.addEventListener("resize", measure);

		if (document.fonts?.ready) {
			document.fonts.ready.then(measure);
		}

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [textRef, suffixRef]);

	useEffect(() => {
		scheduleMeasure(measure);
	}, [textRef, suffixRef]);

	return shouldAddMargin;
};

export default useSuffixMarginLeft;
